import type { SourceMeta } from '../types';
import { audioCtx } from './context';
import {
  channelsToBuffer,
  pitchShift,
  reverseChannels,
  sliceChannels,
  timeStretch,
} from './warp';

/**
 * Out-of-store home for sample data.
 *
 * `samples` holds the decoded file per source id. `sliceCache` memoises every
 * chopped / warped / reversed / pitched variant we've had to synthesise, keyed
 * by the parameters that produced it, so scrubbing a pattern doesn't re-run
 * WSOLA on every scheduler tick.
 */

export interface SampleEntry {
  buffer: AudioBuffer;
  peaks: Float32Array;
}

/** Crossfade window between consecutive slices, seconds. */
export const SLICE_XFADE = 0.003;

export interface SliceAudio {
  buffer: AudioBuffer;
  /**
   * Seconds of lead-in at the head of `buffer`, taken from the audio before
   * the slice. Playback starts this much early and fades in across it.
   */
  preroll: number;
}

const samples = new Map<string, SampleEntry>();
const sliceCache = new Map<string, SliceAudio>();
/** Cache entries are evicted wholesale when a source's chop grid changes. */
const cacheKeysBySource = new Map<string, Set<string>>();

export function putSample(id: string, entry: SampleEntry): void {
  samples.set(id, entry);
}

export function getSample(id: string | null | undefined): SampleEntry | undefined {
  return id ? samples.get(id) : undefined;
}

export function dropSample(id: string): void {
  samples.delete(id);
  invalidateSource(id);
}

export function invalidateSource(id: string): void {
  const keys = cacheKeysBySource.get(id);
  if (keys) {
    for (const k of keys) sliceCache.delete(k);
    keys.clear();
  }
}

export interface SliceRequest {
  source: SourceMeta;
  slice: number;
  reverse: boolean;
  /** Semitones (track + step + source root, already summed). */
  pitch: number;
  /** >1 makes the slice longer. Applied with WSOLA, pitch preserved. */
  stretch: number;
}

function keyFor(r: SliceRequest): string {
  return [
    r.source.id,
    r.slice,
    r.reverse ? 'r' : '_',
    r.pitch.toFixed(2),
    r.stretch.toFixed(4),
  ].join('|');
}

/**
 * Returns the audio for one slice with all offline processing baked in.
 * Anything that can be done cheaply at playback time (gain, pan, filter,
 * small rate changes) is left to the node graph instead.
 */
export function getSliceBuffer(req: SliceRequest): SliceAudio | null {
  const entry = samples.get(req.source.id);
  if (!entry) return null;
  const edges = req.source.sliceEdges;
  if (req.slice < 0 || req.slice + 1 >= edges.length) return null;

  const key = keyFor(req);
  const hit = sliceCache.get(key);
  if (hit) return hit;

  const startSec = edges[req.slice];
  const endSec = edges[req.slice + 1];
  // A reversed slice has no meaningful lead-in — its head is the original
  // decay, so there is no transient to protect and nothing to reconstruct.
  const pre = req.reverse ? 0 : Math.min(SLICE_XFADE, startSec);

  let ch = sliceChannels(entry.buffer, startSec - pre, endSec);
  if (req.reverse) ch = reverseChannels(ch);
  let preroll = pre;
  if (Math.abs(req.stretch - 1) > 1e-3) {
    ch = timeStretch(ch, req.stretch);
    // WSOLA scales uniformly, lead-in included.
    preroll = pre * req.stretch;
  }
  if (Math.abs(req.pitch) > 1e-3) ch = pitchShift(ch, req.pitch);

  const audio: SliceAudio = {
    buffer: channelsToBuffer(audioCtx(), ch, entry.buffer.sampleRate),
    preroll,
  };
  sliceCache.set(key, audio);
  let set = cacheKeysBySource.get(req.source.id);
  if (!set) {
    set = new Set();
    cacheKeysBySource.set(req.source.id, set);
  }
  set.add(key);
  return audio;
}

export function cacheSize(): number {
  return sliceCache.size;
}
